import React, { useEffect } from 'react';
import { useTheme } from '@emotion/react';
import { NoticeBanner } from '@/molecules';
import { NoticeColor } from '@/constants';

type Notification = {
  text: string;
  type: 'warning';
  actionText: string;
  actionHandler: React.MouseEventHandler | null;
  hide: boolean | null;
  color: NoticeColor;
  fired: boolean;
};

/**
 * Create a notification object for use in state for Livefire.
 */
export function createNotification(options: Partial<Notification> = {}) {
  // only allow the object properties we want to propagate into our notification
  const notification: Notification = {
    text: '',
    type: 'warning',
    actionText: '',
    actionHandler: null,
    hide: null,
    color: 'recordRed',
    fired: false,
  };

  return {
    ...notification,
    ...options,
  };
}

export function useNotification(
  notificationState: Notification,
  setNotification: React.Dispatch<React.SetStateAction<Notification>>,
) {
  const theme = useTheme();
  const notificationConditionalProps = theme.isRebrand ? {} : { color: notificationState.color };
  const notificationBanner = notificationState.text ? (
    <NoticeBanner
      type={notificationState.type}
      noticeText={notificationState.text}
      actionText={notificationState.actionText}
      onActionClick={notificationState.actionHandler}
      isRebrand={theme.isRebrand}
      {...notificationConditionalProps}
    />
  ) : null;

  // hide the notification later
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    if (notificationState.hide) {
      const time =
        typeof notificationState.hide === 'number' && Number.isInteger(notificationState.hide)
          ? notificationState.hide
          : 2000;
      timeout = setTimeout(() => {
        setNotification(createNotification());
      }, time);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
    // include sets so we don't disable the rule
  }, [notificationState.text, notificationState.hide, setNotification]);

  return notificationBanner;
}
